import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from "@/context/auth";
import  Button  from "@/components/button";
import { Footer } from "@/components/footer";
import { AlertCircle, CheckCircle2 } from "lucide-react";

function StripeConnectCallback() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userId } = Route.useParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    console.log('Stripe Connect callback received', {
      userId,
      searchParams: window.location.search,
      hash: window.location.hash
    });

    const urlParams = new URLSearchParams(window.location.search);
    const accountId = urlParams.get('account'); // Connect account ID
    const redirectStatus = urlParams.get('redirect_status');

    console.log('Parsed URL parameters:', {
      accountId,
      redirectStatus,
      userId
    });

    // Handle Connect hosted onboarding completion
    if (redirectStatus === 'succeeded') {
      setStatus('success');
      setMessage(t('stripeCallback.connectSuccessMessage'));

      // Wait for webhook to process, then redirect to dashboard
      setTimeout(() => {
        const role = user?.role || 'worker';
        navigate({
          to: '/app/$role/dashboard',
          params: { role }
        });
      }, 3000);
    } else if (redirectStatus === 'failed') {
      setStatus('error');
      setMessage(t('stripeCallback.connectFailedMessage'));
    } else {
      // No valid status, redirect to dashboard
      console.log('No redirect status found, redirecting to dashboard');
      const role = user?.role || 'worker';
      navigate({
        to: '/app/$role/dashboard',
        params: { role }
      });
    }
  }, [navigate, user, userId, t]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="max-w-md w-full mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">{t('stripeCallback.processing')}</h2>
              <p className="text-gray-600">{t('stripeCallback.processingWait')}</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="text-center bg-white rounded-lg shadow-sm p-8">
            {status === 'success' ? (
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            ) : (
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            )}

            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              {status === 'success' ? t('stripeCallback.setupCompleteTitle') : t('stripeCallback.setupFailedTitle')}
            </h2>

            <p className="text-gray-600 mb-6">{message}</p>

            <div className="space-y-3">
              <Button
                onClick={() => {
                  const role = user?.role || 'worker';
                  navigate({
                    to: '/app/$role/dashboard',
                    params: { role }
                  });
                }}
                className="w-full"
              >
                {t('stripeCallback.continueToDashboard')}
              </Button>

              {status === 'error' && (
                <Button
                  variant="outline"
                  onClick={() => {
                    // Return to setup page to retry
                    const role = user?.role || 'worker';
                    navigate({
                      to: '/app/$role/setup',
                      params: { role }
                    });
                  }}
                  className="w-full"
                >
                  {t('stripeCallback.returnToSetup')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/payment/return/$userId')({
  component: StripeConnectCallback,
});
